// Single source of truth for appointment-status colors across the admin portal.
//
// Grouped semantic palette (one color per lifecycle group):
//   DEAD       red      — cancelled
//   REFUNDED   stone    — refunded (dead, but the money moved)
//   INACTIVE   slate    — unscheduled / on_hold (dormant)
//   PENDING    yellow   — every pending_* (waiting on therapist/patient/payment/mitra)
//   SCHEDULED  blue     — scheduled (confirmed time)
//   DONE       emerald  — paid / completed
//
// To make a single status pop again (e.g. give pending_mitra_approval its own
// hue), just change its entry in STATUS_HUE below — every consumer follows.
//
// Consumed by lib/appointments/utils.ts (grid/list/detail), Orders.tsx (orders
// table), and the therapist schedule calendar block. Tailwind needs literal
// class strings (no `bg-${hue}` interpolation), so colors are kept as full
// literals keyed by hue (BADGE_BY_HUE / BLOCK_BY_HUE keep the full palette so
// regrouping is a one-line change).

import type { VariantDotBadge } from "@/components/shared/dot-badge";
import {
	type Appointment,
	AppointmentStatuses,
} from "@/types/admin-portal/appointment";

type AppointmentStatusKey = keyof typeof AppointmentStatuses;

export type AppointmentStatusHue =
	| "red"
	| "slate"
	| "stone"
	| "orange"
	| "amber"
	| "yellow"
	| "violet"
	| "blue"
	| "emerald"
	| "teal";

export const STATUS_HUE: Record<AppointmentStatusKey, AppointmentStatusHue> = {
	cancelled: "red",
	// Money returned, visit will not happen. Stone rather than red so it is not
	// mistaken for a cancellation at a glance, and rather than slate because
	// unscheduled and on_hold already own that.
	refunded: "stone",
	unscheduled: "slate",
	on_hold: "slate",
	pending_therapist_assignment: "yellow",
	pending_patient_approval: "yellow",
	tentative: "yellow",
	pending_payment: "yellow",
	pending_mitra_approval: "yellow",
	scheduled: "blue",
	paid: "emerald",
	completed: "emerald",
};

// Badge / pill (with border + dark mode) — used in tables, lists, details.
// Hover stays in-hue so it overrides the host <Badge> variant's default hover
// (e.g. hover:bg-primary/80) instead of flashing to the primary color.
const BADGE_BY_HUE: Record<AppointmentStatusHue, string> = {
	red: "text-red-800 bg-red-100 border-red-400 hover:bg-red-200 dark:bg-red-900 dark:text-red-100 dark:border-red-500 dark:hover:bg-red-800",
	slate:
		"text-slate-800 bg-slate-100 border-slate-400 hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:border-slate-500 dark:hover:bg-slate-800",
	stone:
		"text-stone-800 bg-stone-100 border-stone-400 hover:bg-stone-200 dark:bg-stone-900 dark:text-stone-100 dark:border-stone-500 dark:hover:bg-stone-800",
	orange:
		"text-orange-800 bg-orange-100 border-orange-400 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-100 dark:border-orange-500 dark:hover:bg-orange-800",
	amber:
		"text-amber-800 bg-amber-100 border-amber-400 hover:bg-amber-200 dark:bg-amber-900 dark:text-amber-100 dark:border-amber-500 dark:hover:bg-amber-800",
	yellow:
		"text-yellow-800 bg-yellow-100 border-yellow-400 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-100 dark:border-yellow-500 dark:hover:bg-yellow-800",
	violet:
		"text-violet-800 bg-violet-100 border-violet-400 hover:bg-violet-200 dark:bg-violet-900 dark:text-violet-100 dark:border-violet-500 dark:hover:bg-violet-800",
	blue: "text-blue-800 bg-blue-100 border-blue-400 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-500 dark:hover:bg-blue-800",
	emerald:
		"text-emerald-800 bg-emerald-100 border-emerald-400 hover:bg-emerald-200 dark:bg-emerald-900 dark:text-emerald-100 dark:border-emerald-500 dark:hover:bg-emerald-800",
	teal: "text-teal-800 bg-teal-100 border-teal-400 hover:bg-teal-200 dark:bg-teal-900 dark:text-teal-100 dark:border-teal-500 dark:hover:bg-teal-800",
};

// Solid block fill — used by the schedule calendar appointment blocks.
const BLOCK_BY_HUE: Record<AppointmentStatusHue, string> = {
	red: "text-red-800 bg-red-100 dark:bg-red-900 dark:text-red-100",
	slate: "text-slate-800 bg-slate-100 dark:bg-slate-900 dark:text-slate-100",
	stone: "text-stone-800 bg-stone-100 dark:bg-stone-900 dark:text-stone-100",
	orange:
		"text-orange-800 bg-orange-100 dark:bg-orange-900 dark:text-orange-100",
	amber: "text-amber-800 bg-amber-100 dark:bg-amber-900 dark:text-amber-100",
	yellow:
		"text-yellow-800 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-100",
	violet:
		"text-violet-800 bg-violet-100 dark:bg-violet-900 dark:text-violet-100",
	blue: "text-blue-800 bg-blue-100 dark:bg-blue-900 dark:text-blue-100",
	emerald:
		"text-emerald-800 bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-100",
	teal: "text-teal-800 bg-teal-100 dark:bg-teal-900 dark:text-teal-100",
};

const FALLBACK_BADGE = "text-foreground bg-background border-border";
const FALLBACK_BLOCK = "text-foreground bg-muted";

// Map enum value ("PENDING MITRA APPROVAL") back to its key, so callers can pass
// either the snake_case key or the raw status value.
const VALUE_TO_KEY = Object.fromEntries(
	Object.entries(AppointmentStatuses).map(([key, value]) => [value, key]),
) as Record<string, AppointmentStatusKey>;

function toStatusKey(
	status: string | null | undefined,
): AppointmentStatusKey | undefined {
	if (!status) return undefined;
	if (status in STATUS_HUE) return status as AppointmentStatusKey;
	return VALUE_TO_KEY[status];
}

export function appointmentStatusHue(
	status: Appointment["status"] | string | null | undefined,
): AppointmentStatusHue | undefined {
	const key = toStatusKey(status);
	return key ? STATUS_HUE[key] : undefined;
}

export function appointmentBadgeClass(
	status: Appointment["status"] | string | null | undefined,
): string {
	const key = toStatusKey(status);
	return key ? BADGE_BY_HUE[STATUS_HUE[key]] : FALLBACK_BADGE;
}

export function appointmentBlockClass(
	status: Appointment["status"] | string | null | undefined,
): string {
	const key = toStatusKey(status);
	return key ? BLOCK_BY_HUE[STATUS_HUE[key]] : FALLBACK_BLOCK;
}

export function appointmentDotVariant(
	status: Appointment["status"] | string | null | undefined,
): VariantDotBadge["variant"] {
	const key = toStatusKey(status);
	return key ? STATUS_HUE[key] : "outline";
}
